import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  CreateCompanyRequest,
  GenerateCompanyDescriptionRequest,
  GetCompaniesResponse,
  GetInterviewResponse,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
} from '@pkg/types/api'

import api from '@/api'
import { INTERVIEW_KEY, useInterview } from '@/react-query/interview/interview'

export const COMPANY_KEY = 'company'
const USER_COMPANIES_KEY = 'userCompanies'

export const useUserCompanies = () => {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [USER_COMPANIES_KEY],
    queryFn: async () => {
      const companies = (await api.getCompanies()).data.companies || []

      companies.forEach((company) => {
        queryClient.setQueryData([COMPANY_KEY, company.id], company)
      })
      return companies
    },
  })
}

export const useCompany = () => {
  const companyId = useInterview().data?.companyId

  return useQuery({
    queryKey: [COMPANY_KEY, companyId],
    queryFn: async () => {
      if (!companyId) {
        return null
      }
      return (await api.getCompany(companyId)).data.company
    },
    enabled: !!companyId,
  })
}

export const useCreateCompany = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: Omit<CreateCompanyRequest, 'interviewId'>) => {
      if (!interviewId) {
        throw new Error('Interview id is not set')
      }

      const response = await api.createCompany({
        company: data.company,
        roleId: data.roleId,
      })

      const newData = response.data
      queryClient.setQueryData(
        [USER_COMPANIES_KEY],
        (oldCompanies: GetCompaniesResponse['companies']) => [
          ...oldCompanies,
          newData.company,
        ],
      )
      queryClient.setQueryData(
        [COMPANY_KEY, newData.company.id],
        newData.company,
      )
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => ({
          ...oldData,
          companyId: newData.company.id,
        }),
      )

      return response.data
    },
  })
}

export const useUpdateCompany = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateCompanyRequest) => {
      const response = await api.updateCompany(data)
      return response.data
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [COMPANY_KEY, data.company.id],
        (oldCompany: UpdateCompanyResponse['company']) => ({
          ...oldCompany,
          ...data.company,
        }),
      )
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [COMPANY_KEY, data.company.id],
        (oldCompany: UpdateCompanyResponse['company']) => ({
          ...oldCompany,
          ...data.company,
        }),
      )
    },
  })
}

export const useGenerateCompanyDescription = () => {
  return useMutation({
    mutationFn: async (data: GenerateCompanyDescriptionRequest) => {
      return (await api.generateCompanyDescription(data)).data.description
    },
  })
}
